<template>
  <div>
    <edit-deal
      v-if="dataLoaded"
      ref="editDeal"
      :is-new="isNew"
      :all-media-owners="suppliers"
      :is-exchange="true"
      :exchange-i-d="exchange.id"
      :deal="deal"
      :all-media-types="mediaTypes"
      :all-networks="networks"
      :buyer-organization-d-s-ps="organizationDSPs"
      :targeting-criteria="targetingCriteria"
      :is-daypart-section-visible="exchangeSettings.uiEnablePackageDaypart"
      :is-ad-duration-section-visible="exchangeSettings.uiEnablePackageAdDuration"
      :is-site-targeting-enabled="true"
      :is-unit-targeting-enabled="true"
      :is-geofence-group-targeting-enabled="exchangeSettings.uiEnableGeofenceGroupTargeting"
      :is-mobile-retargeting-enabled-for-u-i="exchangeSettings.uiEnableLineItemMobileRetargeting"
      :is-check-avails-enabled="exchangeSettings.uiEnablePackageCheckAvails"
      :is-custom-demographics-enabled="exchangeSettings.uiEnableCustomAudienceTargeting"
      :external-avails-url="exchangeSettings.externalAvailsUrl"
      :delivery-statistics="deliveryStatistics"
      :show-audience-c-p-m="exchangeSettings.uiEnableAudienceCPM"
      :force-network-selection="exchangeSettings.uiForceNetworkSelectionInLineitemsAndDeals"
      :default-state-for-hiding-unit-information-in-bid-requests="
        exchangeSettings.defaultStateForHidingUnitInformationInBidRequests
      "
      :show-hide-unit-information-in-bid-requests="exchangeSettings.canHideUnitInformationInBidRequests"
      @save-package="handleSavePackage"
      @refresh-audience-stats="handleRefreshAudienceStats" />
  </div>
</template>

<script>
  import { capitalize } from 'vue';
  import { mapActions, mapState } from 'vuex';

  import EditDeal from '@/common/components/deals/EditDeal';
  import { DeliveryStatistics } from '@/common/models';
  import ErrorHandler from '@/ErrorHandler';
  import {
    useMediaTypes,
    useNetworks,
    useOrganizationDSPs,
    useTargetingCriteria,
  } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  export default {
    name: 'EditDealPage',
    components: { EditDeal },
    data() {
      return {
        deliveryStatistics: new DeliveryStatistics(),
        dataLoaded: false,
        organizationDSPsQuery: useOrganizationDSPs(),
        networksQuery: useNetworks(),
        targetingCriteriaQuery: useTargetingCriteria(),
        mediaTypesQuery: useMediaTypes(),
      };
    },
    computed: {
      ...mapState('exchApp', {
        deal: (state) => state.package,
        exchange: (state) => state.exchange,
        exchangeSettings: (state) => state.exchangeSettings,
        suppliers: (state) => state.suppliers,
      }),
      isNew() {
        return this.$route.params.id === 'new';
      },
      organizationDSPs() {
        return this.organizationDSPsQuery.items.value;
      },
      networks() {
        return this.networksQuery.items.filter((network) => network.isActive);
      },
      targetingCriteria() {
        return this.targetingCriteriaQuery.items.value;
      },
      mediaTypes() {
        return this.mediaTypesQuery.items.value;
      },
    },
    async beforeMount() {
      this.dataLoaded = false;
      await waitForPendingQueries([
        this.networksQuery,
        this.organizationDSPsQuery,
        this.targetingCriteriaQuery,
        this.mediaTypesQuery,
      ]);
      await this.getSuppliers().catch((err) => {
        ErrorHandler.handleExceptionInUIComponent(err);
      });

      if (this.isNew) {
        window.document.title = capitalize(i18n.global.t('actions.newDeal'));
        await this.createNewPackage();
        this.deal.exchangeID = this.exchange.id;
      } else {
        await this.getPackage({ id: this.$route.params.id }).catch((err) => {
          ErrorHandler.handleExceptionInUIComponent(err);
        });
        window.document.title = `${window.document.title}: ${this.deal.name}`;
      }
      this.dataLoaded = true;
    },
    methods: {
      ...mapActions('exchApp', [
        'createNewPackage',
        'getPackage',
        'getSuppliers',
        'getEstimatedDeliveryStatistics',
        'getEstimatedDeliveryStatisticsScreenAndSiteCount',
        'createPackage',
        'updatePackage',
      ]),
      async handleSavePackage({ deal, callback }) {
        const savefn = this.isNew ? this.createPackage({ instances: [deal] }) : this.updatePackage({ instance: deal });
        const saved = await savefn;
        const savedPackage = this.isNew ? saved[0] : saved;
        if (callback) {
          callback();
        }
        this.$router.push(savedPackage.endpoint);
        UserNotifications.success({
          title: capitalize(i18n.global.t('names.success', { n: 1 })),
          message: i18n.global.t('messages.packageSavedSuccessfully'),
        });
      },
      async handleRefreshAudienceStats(targetParams /* , cpmTargetParams */) {
        // TODO: get CPM once we figure it out on the backend
        const [/* cpmInfo, */ deliveryStatistics] = await Promise.all([
          // this.getCPMForTargets(cpmTargetParams),
          this.getEstimatedDeliveryStatistics({ params: targetParams }),
        ]);
        // this.deal.cpmCalculator.calculateCPMFromRateCard(cpmInfo, deliveryStatistics);
        this.deliveryStatistics = deliveryStatistics;
      },
    },
  };
</script>

<style scoped></style>
